import { useReducedMotion } from 'framer-motion'
import { ArrowUp } from 'lucide-react'
import {
  type ClipboardEvent,
  type FocusEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { cn } from '~/shared/lib/utils'
import { PulsifyLoader } from '~/shared/ui/pulsify-loader'

const PLACEHOLDER_INTERVAL_MS = 3200
const SPRING = 'cubic-bezier(0.175, 0.885, 0.32, 1.275)'
const SIZE = {
  collapsedHeight: 56,
  collapsedWidth: 560,
  expandedWidth: 680,
  expandedMin: 148,
  textMin: 88,
  textMax: 176,
  actionsRow: 60,
} as const
const ROUND_BUTTON =
  'absolute right-2 bottom-2 z-[2] flex size-10 items-center justify-center rounded-full outline-none transition-[background-color,color,box-shadow,transform] duration-300 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card active:scale-[0.94]'

interface PromptFieldProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  label: string
  submitLabel: string
  /** Shown one after another while the field is empty. */
  placeholders: string[]
  canSubmit?: boolean
  busy?: boolean
  /** Controls in the bottom row while the field is open. */
  actions?: ReactNode
  /** Keeps the field open, e.g. while pictures are attached. */
  open?: boolean
  inputMode?: 'text' | 'url'
  autoFocus?: boolean
  onPaste?: (event: ClipboardEvent<HTMLTextAreaElement>) => void
  className?: string
}

function usePlaceholderIndex(count: number, active: boolean): number {
  const [index, setIndex] = useState(0)
  useEffect(() => {
    if (!active || count < 2) return
    const timer = window.setInterval(
      () => setIndex((i) => (i + 1) % count),
      PLACEHOLDER_INTERVAL_MS,
    )
    return () => window.clearInterval(timer)
  }, [active, count])
  return index
}

// Collapses without a transition to read the true content height, then restores
// the old height so the browser animates from it to the new one.
function fitTextarea(el: HTMLTextAreaElement, min: number, max: number): number {
  const previous = el.style.height
  el.style.transition = 'none'
  el.style.height = '0px'
  const next = Math.max(min, Math.min(el.scrollHeight, max))
  el.style.height = previous
  el.getBoundingClientRect()
  el.style.transition = ''
  el.style.height = `${next}px`
  el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden'
  return next
}

/** One prompt input that opens smoothly on focus, shared by every "paste it, we fill it" start. */
export function PromptField({
  value,
  onChange,
  onSubmit,
  label,
  submitLabel,
  placeholders,
  canSubmit,
  busy = false,
  actions,
  open: keepOpen = false,
  inputMode = 'text',
  autoFocus = false,
  onPaste,
  className,
}: PromptFieldProps) {
  const reduceMotion = useReducedMotion()
  const rootRef = useRef<HTMLFormElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [focused, setFocused] = useState(false)
  const [smooth, setSmooth] = useState(false)
  const [textHeight, setTextHeight] = useState<number>(SIZE.textMin)
  const isEmpty = value.length === 0
  const open = focused || !isEmpty || busy || keepOpen
  const ready = (canSubmit ?? value.trim().length > 0) && !busy
  const placeholderIndex = usePlaceholderIndex(placeholders.length, isEmpty)
  const placeholder = placeholders[placeholderIndex % Math.max(placeholders.length, 1)] ?? ''

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure whenever the text changes
  useLayoutEffect(() => {
    if (!inputRef.current) return
    if (!open) {
      inputRef.current.style.height = ''
      return
    }
    setTextHeight(fitTextarea(inputRef.current, SIZE.textMin, SIZE.textMax))
  }, [value, open])

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  const onBlur = (event: FocusEvent<HTMLFormElement>) => {
    if (rootRef.current?.contains(event.relatedTarget as Node | null)) return
    setSmooth(false)
    setFocused(false)
  }

  const transition = reduceMotion
    ? 'none'
    : smooth
      ? 'height 0.15s ease-out, max-width 0.15s ease-out'
      : `height 0.4s ${SPRING}, max-width 0.4s ${SPRING}`
  const height = open
    ? Math.max(SIZE.expandedMin, textHeight + SIZE.actionsRow)
    : SIZE.collapsedHeight

  return (
    <form
      ref={rootRef}
      onSubmit={(event) => {
        event.preventDefault()
        if (ready) onSubmit()
      }}
      onBlur={onBlur}
      className={cn('relative mx-auto flex w-full flex-col text-left', className)}
      style={{ maxWidth: open ? SIZE.expandedWidth : SIZE.collapsedWidth, transition }}
    >
      <div
        style={{ height, transition }}
        className={cn(
          'relative z-10 w-full rounded-[28px] border border-border bg-card shadow-[0_14px_36px_-20px_color-mix(in_oklch,var(--foreground)_40%,transparent)] transition-[border-color,box-shadow] motion-reduce:transition-none',
          'focus-within:border-brand/45 focus-within:shadow-[0_0_0_4px_color-mix(in_oklch,var(--brand)_12%,transparent),0_14px_36px_-20px_color-mix(in_oklch,var(--foreground)_40%,transparent)]',
          open ? 'cursor-text' : 'cursor-default',
        )}
      >
        <textarea
          ref={inputRef}
          rows={1}
          value={value}
          readOnly={busy}
          aria-label={label}
          inputMode={inputMode}
          autoCapitalize={inputMode === 'url' ? 'none' : undefined}
          autoComplete={inputMode === 'url' ? 'url' : 'off'}
          spellCheck={inputMode !== 'url'}
          enterKeyHint="send"
          onFocus={() => {
            setSmooth(false)
            setFocused(true)
          }}
          onChange={(event) => {
            setSmooth(true)
            onChange(event.target.value)
          }}
          onPaste={onPaste}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && isEmpty) {
              setSmooth(false)
              setFocused(false)
              event.currentTarget.blur()
              return
            }
            if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return
            event.preventDefault()
            if (ready) onSubmit()
          }}
          className={cn(
            'prompt-field-scroll absolute inset-x-0 top-0 z-[1] block w-full resize-none bg-transparent pr-14 text-[17px] text-foreground leading-7 outline-none transition-[height,padding] duration-300 ease-out motion-reduce:transition-none',
            open ? 'pt-4 pb-2 pl-5' : 'pt-3.5 pb-2 pl-5',
          )}
        />
        {open && isEmpty ? (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 truncate pt-4 pr-14 pl-5 text-[17px] text-muted-foreground/80 leading-7"
          >
            <span key={placeholderIndex} className="prompt-field-placeholder-in inline-block">
              {placeholder}
            </span>
          </span>
        ) : null}

        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-y-0 right-14 left-5 z-[1] flex items-center truncate text-[17px] text-muted-foreground/80 transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] motion-reduce:transition-none',
            open ? 'translate-y-1 scale-105 opacity-0' : 'translate-y-0 scale-100 opacity-100',
          )}
        >
          <span key={placeholderIndex} className="prompt-field-placeholder-in inline-block">
            {placeholder}
          </span>
        </span>

        {actions ? (
          <div
            inert={!open}
            className={cn(
              'absolute bottom-2.5 left-3 z-[2] flex max-w-[calc(100%-4.5rem)] items-center gap-2 transition-[opacity,transform,filter] duration-300 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] motion-reduce:transition-none',
              open
                ? 'translate-y-0 opacity-100 blur-0'
                : 'pointer-events-none translate-y-2 opacity-0 blur-sm',
            )}
          >
            {actions}
          </div>
        ) : null}

        <button
          type="submit"
          aria-label={submitLabel}
          title={submitLabel}
          disabled={!ready}
          onMouseDown={(event) => event.preventDefault()}
          className={cn(
            ROUND_BUTTON,
            ready || busy
              ? 'bg-brand text-brand-foreground shadow-[0_10px_22px_-10px_var(--brand)] hover:brightness-105'
              : 'bg-foreground/[0.08] text-muted-foreground dark:bg-foreground/10',
          )}
        >
          {busy ? (
            <PulsifyLoader tone="on-brand" className="h-6 w-auto" />
          ) : (
            <ArrowUp
              strokeWidth={2.5}
              className={cn(
                'size-[18px] transition-transform duration-300 ease-[cubic-bezier(0.175,0.885,0.32,1.275)]',
                ready ? 'scale-100' : 'scale-90',
              )}
            />
          )}
        </button>
      </div>
    </form>
  )
}

export function PromptFieldAction({
  children,
  onClick,
  disabled,
  title,
  className,
}: {
  children: ReactNode
  onClick: () => void
  disabled?: boolean
  title?: string
  className?: string
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cn(
        'relative flex h-9 items-center gap-1.5 rounded-full border border-border pr-3.5 pl-2.5 font-medium text-[13px] text-foreground outline-none transition-colors duration-200 hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 [&_svg]:size-4',
        className,
      )}
    >
      {children}
    </button>
  )
}
